import { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, TextField, InputAdornment, Autocomplete, Checkbox, createFilterOptions } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader } from '../../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import PostCheckoutQuery from '../../../services/query/postCheckout/PostCheckoutQuery.service';
import CargoAddressBlockingCommand from '../../../services/command/cargoAddressBlocking/CargoAddressBlockingCommand.services';
import CargoManagementQuery from '../../../services/query/cargoManagement/CargoManagementQuery.services';
import Swal from 'sweetalert2';
import Helper from '../../../utils/helper';
import { CAB, cabCls } from '../cargoAddressBlocking.classes';

// Types
interface SearchDrawerProps {
    isDrawerOpen: boolean;
    handleDrawerClose: () => void;
    handleSearch?: (filterParams: any) => void;
    onAddSuccess?: (filterParams: any) => void;
    mode?: 'search' | 'add';
    cargoCompanyOptions?: Option[];
    cityOptions?: Option[];
    setIsLoading?: (loading: boolean) => void;
    isLoading?: boolean;
}

interface Option {
    id: number;
    name: string;
    parentAddressEntityId?: number;
}

interface FormState {
    selectedCargoCompany: Option | null;
    selectedCity: Option | null;
    selectedCounties: Option[];
    selectedNeighborhoods: Option[];
}

// Constants
const ALL_OPTION_ID = -1;
const DRAWER_WIDTH = '480px';
const ADDRESS_ENTITY_TYPE_CITY = 3;
const ADDRESS_ENTITY_TYPE_COUNTY = 4;
const ADDRESS_ENTITY_TYPE_NEIGHBORHOOD = 5;

// Icons for checkbox
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Initial form state - moved outside component to prevent recreation
const INITIAL_FORM_STATE: FormState = {
    selectedCargoCompany: null,
    selectedCity: null,
    selectedCounties: [],
    selectedNeighborhoods: [],
};

// Create filter options that filters by name and ensures uniqueness by id
const filterOptions = createFilterOptions<Option>({
    matchFrom: 'any',
    stringify: (option) => option.name,
});

const AUTOCOMPLETE_SINGLE_SX = {
    '& .MuiAutocomplete-inputRoot': { minHeight: '50px' },
};
const AUTOCOMPLETE_MULTI_SX = {
    '& .MuiAutocomplete-inputRoot': {
        minHeight: '50px',
        flexWrap: 'wrap',
        maxHeight: '120px',
    },
};

// Helper to build address entity request params
const buildAddressEntityParams = (addressEntityTypeId: number, parentAddressEntityId = 0) => ({
    countryId: Helper.getCountryId(),
    cultureCode: localStorage.getItem('i18nextLng') || 'tr-TR',
    parentAddressEntityId,
    deliveryMethodId: 0,
    addressEntityTypeId,
    isVisible: true,
    isMpStore: true,
});

// Helper to extract address entities from response
const extractAddressEntities = (res: any): Option[] => {
    const addressEntities = res?.data?.addressEntities || res?.addressEntities || [];
    if (Array.isArray(addressEntities)) {
        return addressEntities.map((item: any) => ({
            id: item?.addressEntityId ?? item?.id,
            name: item?.name ?? '',
            parentAddressEntityId: item?.parentAddressEntityId ?? item?.parentId,
        }));
    }
    return [];
};

// Build HTML body for add-result Swal modal
const buildAddResultSwalHtml = (
    t: (key: string) => string,
    totalRequested: number,
    successCount: number,
    failedCount: number,
    message: string
) => `
    <div style="display: flex; justify-content: center; gap: 24px; margin-bottom: 16px;">
        <div style="text-align: center;">
            <div style="font-size: 28px; font-weight: bold; color: #2196F3;">${totalRequested}</div>
            <div style="font-size: 12px; color: #666;">${t('cargoAddressBlocking.totalRequested')}</div>
        </div>
        <div style="text-align: center;">
            <div style="font-size: 28px; font-weight: bold; color: #4CAF50;">${successCount}</div>
            <div style="font-size: 12px; color: #666;">${t('cargoAddressBlocking.successCount')}</div>
        </div>
        <div style="text-align: center;">
            <div style="font-size: 28px; font-weight: bold; color: #F44336;">${failedCount}</div>
            <div style="font-size: 12px; color: #666;">${t('cargoAddressBlocking.failedCount')}</div>
        </div>
    </div>
    ${message ? `<div style="margin-top: 12px; padding: 12px; background-color: #f5f5f5; border-radius: 8px; font-size: 13px; color: #333; text-align: left; max-height: 150px; overflow-y: auto;">${message}</div>` : ''}
`;

export const SearchDrawer = ({
    isDrawerOpen,
    handleDrawerClose,
    handleSearch,
    onAddSuccess,
    mode = 'search',
    cargoCompanyOptions: cargoCompanyOptionsProp = [],
    cityOptions: cityOptionsProp = [],
    setIsLoading,
    isLoading = false,
}: SearchDrawerProps) => {
    const { t } = useTranslation();

    // "Tümü" (Select All) option
    const allOption: Option = useMemo(() => ({ id: ALL_OPTION_ID, name: t('general.all') ?? 'Tümü' }), [t]);

    const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
    const [cargoCompanyOptions, setCargoCompanyOptions] = useState<Option[]>([]);
    const [cityOptions, setCityOptions] = useState<Option[]>([]);
    const [countyOptions, setCountyOptions] = useState<Option[]>([]);
    const [neighborhoodOptions, setNeighborhoodOptions] = useState<Option[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [isLoadingCounties, setIsLoadingCounties] = useState(false);
    const [isLoadingNeighborhoods, setIsLoadingNeighborhoods] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update options when props change
    useEffect(() => {
        if (cargoCompanyOptionsProp.length > 0) {
            setCargoCompanyOptions(cargoCompanyOptionsProp);
        }
    }, [cargoCompanyOptionsProp]);

    useEffect(() => {
        if (cityOptionsProp.length > 0) {
            setCityOptions([...cityOptionsProp].sort((a, b) => a.name.localeCompare(b.name)));
        }
    }, [cityOptionsProp]);

    // Fetch cargo companies on drawer open if not provided
    useEffect(() => {
        if (isDrawerOpen && cargoCompanyOptions.length === 0 && cargoCompanyOptionsProp.length === 0) {
            setIsLoadingOptions(true);
            PostCheckoutQuery.GetShippingCompanies()
                .then((res: any) => {
                    if (res?.shippingCompanies && Array.isArray(res.shippingCompanies)) {
                        const options = res.shippingCompanies
                            .filter(
                                (company: any) =>
                                    company?.shippingCompanyId != null &&
                                    company?.name != null &&
                                    company?.isCargoCompany === true
                            )
                            .map((company: any) => ({
                                id: company.shippingCompanyId,
                                name: company.name ?? '',
                            }));
                        setCargoCompanyOptions(options);
                    }
                })
                .catch((err: any) => {
                    console.error('Error fetching cargo companies:', err);
                })
                .finally(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [isDrawerOpen, cargoCompanyOptions.length, cargoCompanyOptionsProp.length]);

    // Fetch cities on drawer open if not provided
    useEffect(() => {
        if (isDrawerOpen && cityOptions.length === 0 && cityOptionsProp.length === 0) {
            setIsLoadingOptions(true);
            const params = buildAddressEntityParams(ADDRESS_ENTITY_TYPE_CITY);

            CargoManagementQuery.SubAddressEntities(params)
                .then((res: any) => {
                    const cities = extractAddressEntities(res);
                    setCityOptions([...cities].sort((a, b) => a.name.localeCompare(b.name)));
                })
                .catch((err: any) => {
                    console.error('Error fetching cities:', err);
                })
                .finally(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [isDrawerOpen, cityOptions.length, cityOptionsProp.length]);

    // Fetch counties when city changes
    useEffect(() => {
        if (formState.selectedCity) {
            setIsLoadingCounties(true);
            setFormState((prev) => ({ ...prev, selectedCounties: [], selectedNeighborhoods: [] }));
            setNeighborhoodOptions([]);

            const params = buildAddressEntityParams(ADDRESS_ENTITY_TYPE_COUNTY, formState.selectedCity.id);

            CargoManagementQuery.SubAddressEntities(params)
                .then((res: any) => {
                    setCountyOptions(extractAddressEntities(res));
                })
                .catch((err: any) => {
                    console.error('Error fetching counties:', err);
                })
                .finally(() => {
                    setIsLoadingCounties(false);
                });
        } else {
            setCountyOptions([]);
            setNeighborhoodOptions([]);
            setFormState((prev) => ({ ...prev, selectedCounties: [], selectedNeighborhoods: [] }));
        }
    }, [formState.selectedCity]);

    const isNeighborhoodSelectable = useMemo(
        () => formState.selectedCounties.length === 1 && formState.selectedCounties[0].id !== ALL_OPTION_ID,
        [formState.selectedCounties]
    );

    // Fetch neighborhoods only when a single county is selected and it's not "All"
    useEffect(() => {
        if (!isNeighborhoodSelectable) {
            setNeighborhoodOptions([]);
            setFormState((prev) => ({ ...prev, selectedNeighborhoods: [] }));
            return;
        }

        const selectedCountyId = formState.selectedCounties[0].id;
        setIsLoadingNeighborhoods(true);
        setFormState((prev) => ({ ...prev, selectedNeighborhoods: [] }));

        const params = buildAddressEntityParams(ADDRESS_ENTITY_TYPE_NEIGHBORHOOD, selectedCountyId);

        CargoManagementQuery.SubAddressEntities(params)
            .then((res: any) => {
                const neighborhoods = extractAddressEntities(res);
                setNeighborhoodOptions([...neighborhoods].sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch((err: any) => {
                console.error('Error fetching neighborhoods:', err);
                setNeighborhoodOptions([]);
            })
            .finally(() => {
                setIsLoadingNeighborhoods(false);
            });
    }, [formState.selectedCounties, isNeighborhoodSelectable]);

    // Reset form when drawer closes
    useEffect(() => {
        if (!isDrawerOpen) {
            setFormState(INITIAL_FORM_STATE);
            setCountyOptions([]);
            setNeighborhoodOptions([]);
        }
    }, [isDrawerOpen]);

    // Check if all required fields are filled
    const isFormValid = useMemo(
        () =>
            formState.selectedCargoCompany !== null &&
            formState.selectedCity !== null &&
            formState.selectedCounties.length > 0 &&
            (!isNeighborhoodSelectable || formState.selectedNeighborhoods.length > 0),
        [
            formState.selectedCargoCompany,
            formState.selectedCity,
            formState.selectedCounties,
            formState.selectedNeighborhoods,
            isNeighborhoodSelectable,
        ]
    );

    const handleSubmit = useCallback(() => {
        // If cargo company "Tümü" is selected (only possible in search mode),
        // don't filter by cargo company on backend side.
        const cargoCompanyId =
            formState.selectedCargoCompany?.id === ALL_OPTION_ID ? ALL_OPTION_ID : formState.selectedCargoCompany?.id;

        const isAllSelected = formState.selectedCounties.some((d) => d.id === ALL_OPTION_ID);
        const addressEntityIds = isAllSelected
            ? countyOptions.map((c) => c.id)
            : formState.selectedCounties.map((d) => d.id);
        const isAllNeighborhoodSelected = formState.selectedNeighborhoods.some((d) => d.id === ALL_OPTION_ID);
        const neighborhoodIds = isAllNeighborhoodSelected
            ? [ALL_OPTION_ID]
            : formState.selectedNeighborhoods.map((d) => d.id);
        const hasNeighborhoodSelection = formState.selectedNeighborhoods.length > 0;
        const effectiveAddressEntityIds =
            isAllSelected || isAllNeighborhoodSelected
                ? [ALL_OPTION_ID]
                : hasNeighborhoodSelection
                  ? neighborhoodIds
                  : addressEntityIds;
        const selectedAddressEntities = hasNeighborhoodSelection
            ? isAllNeighborhoodSelected
                ? neighborhoodOptions
                : formState.selectedNeighborhoods
            : isAllSelected
              ? countyOptions
              : formState.selectedCounties;
        const parentAddressEntityIds = Array.from(
            new Set(
                selectedAddressEntities
                    .map((item) => item.parentAddressEntityId)
                    .filter((parentId): parentId is number => typeof parentId === 'number' && parentId > 0)
            )
        );

        if (mode === 'search') {
            const filterParams = {
                cargoCompanyId,
                countyIds: effectiveAddressEntityIds,
                parentAddressEntityIds,
            };
            handleSearch?.(filterParams);
        } else if (mode === 'add') {
            setIsSubmitting(true);
            setIsLoading?.(true);

            const payload = {
                shippingCompanyId: cargoCompanyId,
                addressEntityIds: effectiveAddressEntityIds,
                parentAddressEntityIds,
            };

            CargoAddressBlockingCommand.UpsertBlockedAddress(payload)
                .then((res: any) => {
                    const response = res?.data || res || {};
                    const {
                        isSuccess = false,
                        message = '',
                        totalRequested = 0,
                        successCount = 0,
                        failedCount = 0,
                    } = response;

                    Swal.fire({
                        icon: isSuccess ? 'success' : failedCount > 0 && successCount > 0 ? 'warning' : 'error',
                        title: isSuccess ? t('general.success') : t('cargoAddressBlocking.addResult'),
                        html: buildAddResultSwalHtml(t, totalRequested, successCount, failedCount, message),
                        confirmButtonText: t('general.ok'),
                        width: 450,
                    });

                    handleDrawerClose();

                    // Only trigger search if there were successful items
                    if (successCount > 0) {
                        const filterParams = {
                            cargoCompanyId,
                            countyIds: addressEntityIds,
                        };
                        onAddSuccess?.(filterParams);
                    }
                })
                .catch((err: any) => {
                    console.error('Error adding blocked address:', err);
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: t('cargoAddressBlocking.addError'),
                        confirmButtonText: t('general.ok'),
                    });
                })
                .finally(() => {
                    setIsSubmitting(false);
                    setIsLoading?.(false);
                });
        }
    }, [mode, formState, countyOptions, handleSearch, setIsLoading, t, handleDrawerClose, onAddSuccess]);

    const handleClear = useCallback(() => {
        setFormState(INITIAL_FORM_STATE);
        setCountyOptions([]);
    }, []);

    // Form field change handlers
    const handleCargoCompanyChange = useCallback((_: any, newValue: Option | null) => {
        setFormState((prev) => ({ ...prev, selectedCargoCompany: newValue }));
    }, []);

    const handleCityChange = useCallback((_: any, newValue: Option | null) => {
        setFormState((prev) => ({ ...prev, selectedCity: newValue }));
    }, []);

    const handleCountyChange = useCallback(
        (_: any, newValue: Option[]) => {
            // If "All" option is selected, select only "All"
            const hasAllOption = newValue.some((v) => v.id === ALL_OPTION_ID);
            const hadAllOption = formState.selectedCounties.some((v) => v.id === ALL_OPTION_ID);

            if (hasAllOption && !hadAllOption) {
                // User just selected "All" - select only "All"
                setFormState((prev) => ({ ...prev, selectedCounties: [allOption] }));
            } else if (hasAllOption && newValue.length > 1) {
                // User selected another option while "All" was selected - deselect "All"
                setFormState((prev) => ({
                    ...prev,
                    selectedCounties: newValue.filter((v) => v.id !== ALL_OPTION_ID),
                }));
            } else {
                setFormState((prev) => ({ ...prev, selectedCounties: newValue }));
            }
        },
        [formState.selectedCounties, allOption]
    );

    const handleNeighborhoodChange = useCallback(
        (_: any, newValue: Option[]) => {
            const hasAllOption = newValue.some((v) => v.id === ALL_OPTION_ID);
            const hadAllOption = formState.selectedNeighborhoods.some((v) => v.id === ALL_OPTION_ID);

            if (hasAllOption && !hadAllOption) {
                setFormState((prev) => ({ ...prev, selectedNeighborhoods: [allOption] }));
            } else if (hasAllOption && newValue.length > 1) {
                setFormState((prev) => ({
                    ...prev,
                    selectedNeighborhoods: newValue.filter((v) => v.id !== ALL_OPTION_ID),
                }));
            } else {
                setFormState((prev) => ({ ...prev, selectedNeighborhoods: newValue }));
            }
        },
        [formState.selectedNeighborhoods, allOption]
    );

    // County options with "Tümü" prepended
    const countyOptionsWithAll = useMemo(() => [allOption, ...countyOptions], [allOption, countyOptions]);
    const neighborhoodOptionsWithAll = useMemo(
        () => [allOption, ...neighborhoodOptions],
        [allOption, neighborhoodOptions]
    );

    // Cargo company options: prepend "Tümü" only in search mode
    const cargoCompanyOptionsWithAll = useMemo(
        () => (mode === 'search' ? [allOption, ...cargoCompanyOptions] : cargoCompanyOptions),
        [mode, allOption, cargoCompanyOptions]
    );

    // Memoized derived values
    const drawerTitle = useMemo(
        () =>
            mode === 'search' ? t('cargoAddressBlocking.searchDrawerTitle') : t('cargoAddressBlocking.addDrawerTitle'),
        [mode, t]
    );

    const submitButtonText = useMemo(() => (mode === 'search' ? t('general.search') : t('general.save')), [mode, t]);

    const isButtonsDisabled =
        isLoadingOptions || isLoadingCounties || isLoadingNeighborhoods || isSubmitting || isLoading;
    const showLoadingIndicator = isLoadingOptions || isSubmitting || isLoading;

    const isOptionEqualToValue = useCallback((option: Option, value: Option) => option.id === value.id, []);
    const getOptionLabel = useCallback((option: Option) => option.name, []);

    const renderSearchInput = (params: any, label: string, placeholder: string, fieldClass: string) => (
        <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            className={cabCls(CAB.input, CAB.inputTextfield, CAB.inputAutocomplete, fieldClass, params.className)}
            slotProps={{
                input: {
                    ...params.InputProps,
                    startAdornment: (
                        <>
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                        </>
                    ),
                },
            }}
        />
    );

    return (
        <SCUIGeneralDrawer width={DRAWER_WIDTH} open={isDrawerOpen} onClose={handleDrawerClose}>
            <SCUIGeneralDrawerHeader header={drawerTitle} onClose={handleDrawerClose} />
            {showLoadingIndicator && <SCUILoadingLinear display={showLoadingIndicator} />}
            <Box
                className={cabCls(CAB.drawer, mode === 'search' ? CAB.drawerSearch : CAB.drawerAdd, CAB.drawerBody)}
                sx={{ padding: '16px', display: 'flex', flexDirection: 'column' }}
            >
                {/* Form Container */}
                <Box
                    className={CAB.drawerForm}
                    sx={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                    {/* Cargo Company - Single select, "All" option only in search mode */}
                    <Autocomplete
                        className={cabCls(CAB.input, CAB.inputAutocomplete, CAB.fieldCargoCompany)}
                        fullWidth
                        options={cargoCompanyOptionsWithAll}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedCargoCompany}
                        onChange={handleCargoCompanyChange}
                        loading={isLoadingOptions}
                        disabled={isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        sx={AUTOCOMPLETE_SINGLE_SX}
                        renderOption={(props, option) => (
                            <li {...props} key={`cargo-company-${option.id}`}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                `${t('cargoAddressBlocking.cargoCompany')} (*)`,
                                t('general.search') ?? '',
                                CAB.fieldCargoCompany
                            )
                        }
                    />

                    {/* City (İl) - Single select, no "All" option */}
                    <Autocomplete
                        className={cabCls(CAB.input, CAB.inputAutocomplete, CAB.fieldCity)}
                        fullWidth
                        options={cityOptions}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedCity}
                        onChange={handleCityChange}
                        loading={isLoadingOptions}
                        disabled={isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        sx={AUTOCOMPLETE_SINGLE_SX}
                        renderOption={(props, option) => (
                            <li {...props} key={`city-${option.id}`}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                `${t('cargoAddressBlocking.city')} (*)`,
                                t('general.search') ?? '',
                                CAB.fieldCity
                            )
                        }
                    />

                    {/* County (İlçe) - Multiple select with "All" option */}
                    <Autocomplete
                        className={cabCls(CAB.input, CAB.inputAutocomplete, CAB.fieldCounty)}
                        multiple
                        fullWidth
                        limitTags={2}
                        options={countyOptionsWithAll}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedCounties}
                        onChange={handleCountyChange}
                        loading={isLoadingCounties}
                        disabled={!formState.selectedCity || isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        disableCloseOnSelect
                        sx={AUTOCOMPLETE_MULTI_SX}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={`county-${option.id}`}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                    className={cabCls(CAB.input, CAB.inputCheckbox, CAB.cbCountyOption)}
                                />
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                `${t('cargoAddressBlocking.county')} (*)`,
                                formState.selectedCounties.length === 0 ? (t('general.search') ?? '') : '',
                                CAB.fieldCounty
                            )
                        }
                    />

                    {/* Neighborhood (Mahalle) - Multiple select */}
                    <Autocomplete
                        className={cabCls(CAB.input, CAB.inputAutocomplete, CAB.fieldNeighborhood)}
                        multiple
                        fullWidth
                        limitTags={2}
                        options={neighborhoodOptionsWithAll}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedNeighborhoods}
                        onChange={handleNeighborhoodChange}
                        loading={isLoadingNeighborhoods}
                        disabled={!isNeighborhoodSelectable || isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        disableCloseOnSelect
                        sx={AUTOCOMPLETE_MULTI_SX}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={`neighborhood-${option.id}`}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                    className={cabCls(CAB.input, CAB.inputCheckbox, CAB.cbNeighborhoodOption)}
                                />
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                t('cargoAddressBlocking.neighborhood'),
                                formState.selectedNeighborhoods.length === 0 ? (t('general.search') ?? '') : '',
                                CAB.fieldNeighborhood
                            )
                        }
                    />
                </Box>

                {/* Buttons */}
                <Box
                    className={CAB.drawerActions}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        paddingTop: '32px',
                        padding: '12px',
                    }}
                >
                    <SCUIButton
                        text={t('general.clear')}
                        onClick={handleClear}
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                        disabled={isButtonsDisabled}
                        className={cabCls(CAB.input, CAB.inputButton, CAB.btnDrawerClear)}
                    />
                    <SCUIButton
                        text={submitButtonText}
                        type="primary"
                        sizeType="fixed"
                        disabled={!isFormValid || isButtonsDisabled}
                        size={'l'}
                        buttonType="button"
                        onClick={handleSubmit}
                        className={cabCls(CAB.input, CAB.inputButton, CAB.btnDrawerSubmit)}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};
